import { isEmpty, omit } from 'lodash';
import { makeActionCreator, reduce, getIdxById } from '../helpers/utils';
import ApiClient from '../api/Api';
import { notify } from '../helpers/BugsnagClient';

const debug = require('debug')('jp:reducers:payments');

export const INITIALIZE_PAYPAL = 'payments/INITIALIZE_PAYPAL';
export const GET_BRAINTREE_TOKEN = 'payments/GET_BRAINTREE_TOKEN';
export const GET_BRAINTREE_TOKEN_SUCCESS =
  'payments/GET_BRAINTREE_TOKEN_SUCCESS';
export const GET_BRAINTREE_TOKEN_FAIL = 'payments/GET_BRAINTREE_TOKEN_FAIL';
export const BRAINTREE_PAYPAL_FAILED = 'payments/BRAINTREE_PAYPAL_FAILED';
export const BRAINTREE_PAYPAL_INITIALIZED =
  'payments/BRAINTREE_PAYPAL_INITIALIZED';
export const LOAD_PAYMENTMETHODS = 'payments/LOAD_PAYMENTMETHODS';
export const LOAD_PAYMENTMETHODS_SUCCESS =
  'payments/LOAD_PAYMENTMETHODS_SUCCESS';
export const LOAD_PAYMENTMETHODS_FAIL = 'payments/LOAD_PAYMENTMETHODS_FAIL';
export const TOGGLE_USER_ADD_PAYMENTMETHOD =
  'payments/TOGGLE_USER_ADD_PAYMENTMETHOD';
export const CREATE_PAYMENTMETHOD = 'payments/CREATE_PAYMENTMETHOD';
export const CREATE_PAYMENTMETHOD_SUCCESS =
  'payments/CREATE_PAYMENTMETHOD_SUCCESS';
export const CREATE_PAYMENTMETHOD_FAIL = 'payments/CREATE_PAYMENTMETHOD_FAIL';
export const CREATE_PAYPAL_PAYMENTMETHOD =
  'payments/CREATE_PAYPAL_PAYMENTMETHOD';
export const CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS =
  'payments/CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS';
export const CREATE_PAYPAL_PAYMENTMETHOD_FAIL =
  'payments/CREATE_PAYPAL_PAYMENTMETHOD_FAIL';
export const TOGGLE_ALL_PAYMENTMETHODS = 'payments/TOGGLE_ALL_PAYMENTMETHODS';
export const SELECT_PAYMENTMETHOD = 'payments/SELECT_PAYMENTMETHOD';
export const DELETE_PAYMENTMETHOD = 'payments/DELETE_PAYMENTMETHOD';
export const DELETE_PAYMENTMETHOD_SUCCESS =
  'payments/DELETE_PAYMENTMETHOD_SUCCESS';
export const DELETE_PAYMENTMETHOD_FAIL = 'payments/DELETE_PAYMENTMETHOD_FAIL';
export const UPDATE_PAYMENT_TAB = 'payments/UPDATE_PAYMENT_TAB';
export const SET_PAYMENT_DETAILS_LOCK = 'payments/SET_PAYMENT_DETAILS_LOCK';
export const SET_PAYMENT_DETAILS_SUBMITTING =
  'payments/SET_PAYMENT_DETAILS_SUBMITTING';
export const CARD_TOKENIZING = 'payments/CARD_TOKENIZING';
export const ADD_PROMO_CODE = 'payments/ADD_PROMO_CODE';
export const ADD_PROMO_CODE_SUCCESS = 'payments/ADD_PROMO_CODE_SUCCESS';
export const ADD_PROMO_CODE_FAIL = 'payments/ADD_PROMO_CODE_FAIL';
export const FILTER_PAYMENT_METHODS = 'payments/FILTER_PAYMENT_METHODS';
export const HOSTED_FIELDS_ERRORS = 'payments/HOSTED_FIELDS_ERRORS';

type Action =
  | {| type: typeof GET_BRAINTREE_TOKEN |}
  | {| type: typeof LOAD_PAYMENTMETHODS |}
  | {| type: typeof LOAD_PAYMENTMETHODS_SUCCESS, payload: Object |}
  | {| type: typeof LOAD_PAYMENTMETHODS_FAIL |}
  | {| type: typeof TOGGLE_ALL_PAYMENTMETHODS |}
  | {| type: typeof DELETE_PAYMENTMETHOD, id: number |}
  | {| type: typeof DELETE_PAYMENTMETHOD_SUCCESS, id: number |}
  | {| type: typeof DELETE_PAYMENTMETHOD_FAIL, id: number |}
  | {| type: typeof CREATE_PAYMENTMETHOD |}
  | {| type: typeof CREATE_PAYMENTMETHOD_SUCCESS |}
  | {| type: typeof CREATE_PAYMENTMETHOD_FAIL |}
  | {| type: typeof UPDATE_PAYMENT_TAB, index: number |}
  | {| type: typeof SET_PAYMENT_DETAILS_LOCK, lock: boolean |}
  | {| type: typeof CARD_TOKENIZING |}
  | {| type: typeof SET_PAYMENT_DETAILS_SUBMITTING, submitting: boolean |}
  | {| type: typeof TOGGLE_USER_ADD_PAYMENTMETHOD, bool: boolean |}
  | {| type: typeof ADD_PROMO_CODE |}
  | {| type: typeof ADD_PROMO_CODE_SUCCESS |}
  | {| type: typeof ADD_PROMO_CODE_FAIL |}
  | {| type: typeof FILTER_PAYMENT_METHODS, excludedPaymentMethods: Array |}
  | {| type: typeof HOSTED_FIELDS_ERRORS, errors: Object |};

export type PaymentMethod = {|
  id: number,
  source: string,
  authority: ?string,
  isPrimary: boolean,
  isBlocked: boolean,
  isAuthorised: boolean,
  createdAt: string,
  name: string,
  cardType: string,
  lastFour: ?string,
  expiryDate: string,
  email?: string,
  isActive: boolean
|};

export type State = {|
  braintreeToken: ?string,
  braintreePaypalInitialized: boolean,
  braintreePaypalFailed: boolean,
  braintreePaypalLoading: boolean,
  loadingPaymentMethods: boolean,
  loadPaymentMethodsComplete: boolean,
  paymentMethods: PaymentMethod[],
  creatingPaypalPaymentMethod: boolean,
  deletingPaymentMethod: boolean,
  displayAllPaymentMethods: boolean,
  userAddingPaymentMethod: boolean,
  newPaymentMethodAdded: boolean,
  activePaymentTab: number,
  hostedFieldErrors: {
    'credit-card-number'?: string,
    expiration?: string,
    cvv?: string
  },
  apiErrors: {
    availability: ?string
  }
|};

export const initialState: State = {
  apiErrors: {
    availability: null
  },
  braintreeToken: false,
  braintreePaypalInitialized: false,
  braintreePaypalFailed: false,
  braintreePaypalLoading: false,
  loadingPaymentMethods: false,
  loadPaymentMethodsComplete: false,
  paymentMethods: [],
  creatingPaypalPaymentMethod: false,
  hostedFieldErrors: {},
  newPaymentMethodAdded: false,
  paymentDetailsSubmitting: false,
  paymentDetailsLocked: false,
  deletingPaymentMethod: false,
  displayAllPaymentMethods: false,
  activePaymentTab: 0,
  userAddingPaymentMethod: false // assumed false as we don't want to trigger network requests for the iframes unless required
};

const booleanKeySort = key => (a, b) => {
  if (a[key]) {
    return -1;
  }
  if (b[key]) {
    return 1;
  }
  return 0;
};
const isActiveSort = booleanKeySort('isActive');
const isPrimarySort = booleanKeySort('isPrimary');
const getPrimaryPaymentMethodId = (
  methods: PaymentMethod[],
  onlyPayPal: boolean
): ?number => {
  const results = methods
    .filter(item => (item.source === 'paypal') === onlyPayPal)
    .sort(isPrimarySort);

  return results.length ? results[0].id : null;
};

const getActivePaymentTab = paymentMethods =>
  paymentMethods.length && paymentMethods[0].source === 'paypal' ? 1 : 0;

const getNonPaypalMethods = paymentMethods =>
  paymentMethods.filter(method => method.source !== 'paypal');

const noActivePaymentMethod = paymentMethods =>
  !isEmpty(paymentMethods) &&
  paymentMethods.filter(item => item.isActive).length === 0;

const setActivePaymentMethod = paymentMethods => {
  const withActivePaymentMethod = [...paymentMethods];
  withActivePaymentMethod[0].isActive = true;

  return withActivePaymentMethod;
};
export default reduce(initialState, {
  [INITIALIZE_PAYPAL](state: State, action: Action): State {
    debug(INITIALIZE_PAYPAL, action);

    return {
      ...state,
      braintreePaypalLoading: true
    };
  },
  [GET_BRAINTREE_TOKEN_SUCCESS](state: State, action: Action): State {
    debug(GET_BRAINTREE_TOKEN_SUCCESS, action);

    if (action.payload && action.payload.data) {
      return {
        ...state,
        braintreeToken: action.payload.data.token,
        braintreePaypalFailed: false,
        braintreePaypalLoading: false
      };
    }
    return state;
  },
  [GET_BRAINTREE_TOKEN_FAIL](state: State, action: Action): State {
    debug(GET_BRAINTREE_TOKEN_FAIL, action);

    return {
      ...state,
      braintreePaypalFailed: true,
      braintreePaypalLoading: false
    };
  },
  [BRAINTREE_PAYPAL_FAILED](state: State, action: Action): State {
    debug(BRAINTREE_PAYPAL_FAILED, action);

    return {
      ...state,
      braintreePaypalFailed: true,
      braintreePaypalLoading: false
    };
  },
  [BRAINTREE_PAYPAL_INITIALIZED](state: State, action: Action): State {
    debug(BRAINTREE_PAYPAL_INITIALIZED, action);

    return {
      ...state,
      braintreePaypalInitialized: action.initialized
    };
  },
  [LOAD_PAYMENTMETHODS](state: State): State {
    debug(LOAD_PAYMENTMETHODS);
    return { ...state, loadingPaymentMethods: true };
  },
  [LOAD_PAYMENTMETHODS_SUCCESS](state: State, action: Action): State {
    debug(LOAD_PAYMENTMETHODS_SUCCESS, action);

    if (action.payload && action.payload.data) {
      const rawPaymentMethods = action.payload.data;

      const paymentMethods = rawPaymentMethods
        .filter(item => item.cardType && item.cardType.toLowerCase())
        .map(item => ({
          ...item,
          isActive: item.id === rawPaymentMethods[0].id
        }))
        .sort(isActiveSort);

      const activePaymentTab = getActivePaymentTab(paymentMethods);
      const nonPaypalMethods = getNonPaypalMethods(paymentMethods);
      return {
        ...state,
        loadingPaymentMethods: false,
        loadPaymentMethodsComplete: true,
        paymentMethods: noActivePaymentMethod(paymentMethods)
          ? [...setActivePaymentMethod(paymentMethods)]
          : [...paymentMethods],
        activePaymentTab,
        paymentDetailsLocked: !isEmpty(paymentMethods),
        userAddingPaymentMethod:
          nonPaypalMethods.length <= 0 && activePaymentTab === 0
      };
    }

    // Notify error to bugsnag
    notify(new Error('No payment methods in checkout get payment methods'), {
      metaData: action
    });

    return {
      ...state,
      loadingPaymentMethods: false,
      loadPaymentMethodsComplete: true,
      paymentMethods: [],
      activePaymentTab: 0,
      paymentDetailsLocked: false,
      userAddingPaymentMethod: true
    };
  },
  [LOAD_PAYMENTMETHODS_FAIL](state: State, action: Action): State {
    debug(LOAD_PAYMENTMETHODS_FAIL, action);
    return {
      ...state,
      loadingPaymentMethods: false,
      loadPaymentMethodsComplete: false
    };
  },
  [FILTER_PAYMENT_METHODS](state: State, action: Action): State {
    debug(FILTER_PAYMENT_METHODS, action);
    if (
      'excludedPaymentMethods' in action &&
      action.excludedPaymentMethods.length
    ) {
      const { paymentMethods } = state;

      const excludedLower = action.excludedPaymentMethods.map(el =>
        el.toLowerCase()
      );

      const filteredPaymentMethods = paymentMethods.filter(
        el => !excludedLower.includes(el.cardType && el.cardType.toLowerCase())
      );
      const activePaymentTab = getActivePaymentTab(filteredPaymentMethods);
      const nonPaypalMethods = getNonPaypalMethods(filteredPaymentMethods);

      return {
        ...state,
        paymentMethods: noActivePaymentMethod(filteredPaymentMethods)
          ? [...setActivePaymentMethod(filteredPaymentMethods)]
          : [...filteredPaymentMethods],
        activePaymentTab,
        userAddingPaymentMethod:
          nonPaypalMethods.length <= 0 && activePaymentTab === 0
      };
    }
    return state;
  },
  [SELECT_PAYMENTMETHOD](state: State, action: Action): State {
    debug(SELECT_PAYMENTMETHOD, action);
    if (action && action.id) {
      const { id } = action;
      const { paymentMethods } = state;
      const newPaymentMethods = [
        ...paymentMethods.map(paymentMethod => ({
          ...paymentMethod,
          isActive: paymentMethod.id === id
        }))
      ];
      return {
        ...state,
        paymentMethods: newPaymentMethods
      };
    }
    return state;
  },
  [DELETE_PAYMENTMETHOD](state: State): State {
    debug(DELETE_PAYMENTMETHOD);
    return { ...state, deletingPaymentMethod: true };
  },
  [DELETE_PAYMENTMETHOD_SUCCESS](state: State, action: Action): State {
    debug(DELETE_PAYMENTMETHOD_SUCCESS, action);
    if (action && action.id) {
      const { paymentMethods } = state;
      let newPaymentMethods = [...paymentMethods];
      const idx = getIdxById(paymentMethods, action.id);

      newPaymentMethods.splice(idx, 1);

      // If the item we deleted was active set a new active method item
      if (paymentMethods[idx].isActive && newPaymentMethods.length >= 1) {
        const isPayPalTab = state.activePaymentTab === 1;
        const newActiveId = getPrimaryPaymentMethodId(
          newPaymentMethods,
          isPayPalTab
        );

        newPaymentMethods = newPaymentMethods.map(method => ({
          ...method,
          isActive: method.id === newActiveId
        }));
      }

      const nonPaypalMethods = newPaymentMethods.filter(
        item => item.source !== 'paypal'
      );

      return {
        ...state,
        paymentMethods: [...newPaymentMethods],
        deletingPaymentMethod: false,
        userAddingPaymentMethod:
          state.activePaymentTab === 1 ? false : nonPaypalMethods.length === 0
      };
    }

    notify(new Error('No payment method id in delete action'));

    return {
      ...state
    };
  },
  [DELETE_PAYMENTMETHOD_FAIL](state: State, action: Action): State {
    debug(DELETE_PAYMENTMETHOD_FAIL, action);
    return {
      ...state,
      deletingPaymentMethod: false
    };
  },
  [TOGGLE_ALL_PAYMENTMETHODS](state: State): State {
    debug(TOGGLE_ALL_PAYMENTMETHODS);
    return {
      ...state,
      displayAllPaymentMethods: !state.displayAllPaymentMethods
    };
  },
  [TOGGLE_USER_ADD_PAYMENTMETHOD](state: State, action: Action): State {
    debug(TOGGLE_USER_ADD_PAYMENTMETHOD, action);
    if (action && 'bool' in action) {
      const primaryMethodId = getPrimaryPaymentMethodId(
        [...state.paymentMethods],
        false
      );

      // If user is adding a payment method mark all other methods as not active
      // Otherwise mark the first non paypal primary method as active
      const paymentMethods = action.bool
        ? [...state.paymentMethods].map(method => ({
            ...method,
            isActive: false
          }))
        : [...state.paymentMethods].map(method => ({
            ...method,
            isActive: primaryMethodId === method.id
          }));

      return {
        ...state,
        userAddingPaymentMethod: action.bool,
        paymentMethods
      };
    }

    return {
      ...state
    };
  },
  [SET_PAYMENT_DETAILS_SUBMITTING](state: State, action: Action): State {
    debug(SET_PAYMENT_DETAILS_SUBMITTING);

    if (typeof action.submitting === 'boolean') {
      return {
        ...state,
        paymentDetailsSubmitting: action.submitting
      };
    }

    return {
      ...state
    };
  },
  [SET_PAYMENT_DETAILS_LOCK](state: State, action: Action): State {
    debug(SET_PAYMENT_DETAILS_LOCK);

    if (typeof action.lock === 'boolean') {
      return {
        ...state,
        paymentDetailsLocked: action.lock
      };
    }

    return {
      ...state
    };
  },
  [HOSTED_FIELDS_ERRORS](state: State, action: Action): State {
    debug(HOSTED_FIELDS_ERRORS, action);
    if (action && action.errors) {
      return {
        ...state,
        hostedFieldErrors: action.errors
      };
    }

    notify(new Error('No errors in hosted fields errors action'), {
      metaData: action
    });

    return {
      ...state
    };
  },
  [CARD_TOKENIZING](state: State): State {
    debug(CARD_TOKENIZING);

    // When adding a new card they payment source is added, payment details locked and the primary submit and pay button then becomes active
    // However becuase the payment method ID has changed we call off to get a new booking summary, this causes the pay button to become disabled again
    // This causes a weird looking quick transition from enabled -> disabled -> enabled we want to get rid of.
    // newPaymentMethodAdded is stored here so we can keep that button disabled until the summary endpoint clears it

    return {
      ...state,
      newPaymentMethodAdded: true
    };
  },
  [CREATE_PAYMENTMETHOD](state: State): State {
    debug(CREATE_PAYMENTMETHOD);
    // clear our any apiError we previously received from the api trying to create a payment method
    const apiErrors = omit(state.apiErrors, ['paymentMethodId']);
    return { ...state, paymentDetailsSubmitting: true, apiErrors };
  },
  [CREATE_PAYMENTMETHOD_SUCCESS](state: State, action: Action): State {
    debug(CREATE_PAYMENTMETHOD_SUCCESS, action);
    if (action.payload && action.payload.data) {
      const apiErrors = omit(state.apiErrors, ['paymentMethodId']);

      const newItem = action.payload.data;
      const isPayPalTab = state.activePaymentTab === 1;
      const isPayPalCard = newItem.source === 'paypal';

      const newPaymentMethods = [newItem, ...state.paymentMethods].map(
        paymentMethod => ({
          ...paymentMethod,
          // Set the new item as active if we are showing the tab this method belongs in
          isActive:
            isPayPalTab === isPayPalCard && paymentMethod.id === newItem.id
        })
      );

      return {
        ...state,
        apiErrors,
        paymentMethods: newPaymentMethods,
        userAddingPaymentMethod: false,
        paymentDetailsSubmitting: false,
        // Lock payment section if we have added a new payment method for the tab the user is seeing
        paymentDetailsLocked: isPayPalTab === isPayPalCard,
        hostedFieldErrors: {}
      };
    }

    notify(new Error('No payment method in response'), {
      metaData: action
    });

    return {
      ...state,
      paymentDetailsSubmitting: false
    };
  },
  [CREATE_PAYMENTMETHOD_FAIL](state: State, action: Action): State {
    debug(CREATE_PAYMENTMETHOD_FAIL, action);
    if (action.payload && action.payload.data) {
      return {
        ...state,
        globalApiError: action.payload.data,
        paymentDetailsSubmitting: false
      };
    }
    return {
      ...state,
      paymentDetailsSubmitting: false
    };
  },
  [CREATE_PAYPAL_PAYMENTMETHOD](state: State): State {
    debug(CREATE_PAYPAL_PAYMENTMETHOD);
    return {
      ...state,
      creatingPaypalPaymentMethod: true
    };
  },
  [CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS](state: State, action: Action): State {
    debug(CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS);

    if (action.payload && action.payload.data) {
      const newPaymentMethods = [
        ...state.paymentMethods,
        action.payload.data
      ].map(paymentMethod => ({
        ...paymentMethod,
        isActive:
          paymentMethod.id === action.payload.data.id &&
          state.activePaymentTab === 1
      }));

      return {
        ...state,
        newPaymentMethodAdded: true,
        paymentDetailsLocked: state.activePaymentTab === 1,
        paymentMethods: newPaymentMethods,
        creatingPaypalPaymentMethod: false
      };
    }

    notify(new Error('No payment source in response'), {
      metaData: action
    });

    return {
      ...state,
      creatingPaypalPaymentMethod: false,
      globalApiError: {
        code: 100000,
        title:
          'Something went wrong trying to add your paypal payment source. Please try again, or use a different payment method if the problem persists',
        identifier: 'paypal'
      }
    };
  },
  [CREATE_PAYPAL_PAYMENTMETHOD_FAIL](state: State) {
    return {
      ...state,
      creatingPaypalPaymentMethod: false,
      globalApiError: {
        code: 100000,
        title:
          'Something went wrong trying to add your paypal payment source. Please try again, or use a different payment method if the problem persists',
        identifier: 'paypal'
      }
    };
  },
  [UPDATE_PAYMENT_TAB](state: State, action: Action): State {
    debug(UPDATE_PAYMENT_TAB, action);

    if ('index' in action) {
      const tabType = action.index === 0 ? 'card' : 'paypal';
      const { paymentMethods } = state;

      const newId = getPrimaryPaymentMethodId(
        [...paymentMethods],
        action.index === 1
      );

      const newPaymentMethods = [
        ...paymentMethods.map(paymentMethod => ({
          ...paymentMethod,
          isActive: paymentMethod.id === newId
        }))
      ];

      const nonPaypalMethods = paymentMethods.filter(
        method => method.source !== 'paypal'
      );

      return {
        ...state,
        activePaymentTab: action.index,
        paymentMethods: newPaymentMethods,
        // If on paypal close the add new card form
        userAddingPaymentMethod:
          tabType === 'paypal' ? false : nonPaypalMethods.length === 0
      };
    }

    return {
      ...state
    };
  }
});

export function getBraintreeToken() {
  return {
    types: [
      GET_BRAINTREE_TOKEN,
      GET_BRAINTREE_TOKEN_SUCCESS,
      GET_BRAINTREE_TOKEN_FAIL
    ],
    promise: (client: ApiClient) => client.paymentToken.braintree()
  };
}

export const setBraintreePaypalInitialized = makeActionCreator(
  BRAINTREE_PAYPAL_INITIALIZED,
  'initialized'
);

export const selectPaymentMethod = makeActionCreator(
  SELECT_PAYMENTMETHOD,
  'id'
);

export const toggleUserAddPaymentMethod = makeActionCreator(
  TOGGLE_USER_ADD_PAYMENTMETHOD,
  'bool'
);

export function loadPaymentMethods(id: number) {
  return {
    types: [
      LOAD_PAYMENTMETHODS,
      LOAD_PAYMENTMETHODS_SUCCESS,
      LOAD_PAYMENTMETHODS_FAIL
    ],
    promise: (client: ApiClient) => client.paymentMethods.getPaymentMethods(id)
  };
}

export const setPaymentDetailsLock = makeActionCreator(
  SET_PAYMENT_DETAILS_LOCK,
  'lock'
);

export const setPaymentDetailsSubmitting = makeActionCreator(
  SET_PAYMENT_DETAILS_SUBMITTING,
  'submitting'
);

export const handleHostedFieldsErrors = makeActionCreator(
  HOSTED_FIELDS_ERRORS,
  'errors'
);

export const setCardTokenizing = makeActionCreator(CARD_TOKENIZING);

export function createPaymentMethod(nonce: string, model: Object) {
  return (dispatch: (*) => Promise<*>) => {
    const { postalCode } = model;

    return new Promise((resolve, reject) => {
      dispatch({
        types: [
          CREATE_PAYMENTMETHOD,
          CREATE_PAYMENTMETHOD_SUCCESS,
          CREATE_PAYMENTMETHOD_FAIL
        ],
        promise: (client: ApiClient) =>
          client.paymentMethods
            .create(nonce, postalCode)
            .then(paymentMethod => {
              resolve(paymentMethod);
              return paymentMethod;
            })
            .catch(err => {
              reject(err);
              throw err;
            }),
        nonce,
        postalCode
      });
    });
  };
}

export const toggleAllPaymentMethods = makeActionCreator(
  TOGGLE_ALL_PAYMENTMETHODS
);

export function deletePaymentMethod(id: number) {
  return {
    types: [
      DELETE_PAYMENTMETHOD,
      DELETE_PAYMENTMETHOD_SUCCESS,
      DELETE_PAYMENTMETHOD_FAIL
    ],
    promise: (client: ApiClient) =>
      client.paymentMethods.deletePaymentMethod(id),
    id
  };
}

export function createPaypalPaymentMethod(nonce: string) {
  return {
    types: [
      CREATE_PAYPAL_PAYMENTMETHOD,
      CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS,
      CREATE_PAYPAL_PAYMENTMETHOD_FAIL
    ],
    promise: (client: ApiClient) => client.paymentMethods.createPaypal(nonce)
  };
}

export const updatePaymentTab = makeActionCreator(UPDATE_PAYMENT_TAB, 'index');
export const filterPaymentMethods = makeActionCreator(
  FILTER_PAYMENT_METHODS,
  'excludedPaymentMethods'
);
